import { ActionTypes } from '@/components/Store.d';
import { Banner } from '../Banner/Banner';
import { Context } from '@/components/Store';
import { isSupporter } from '@/lib/util';
import { languageStrings } from '@/l18n/de';
import { ProductPrice } from '@/components/ProductPrice/ProductPrice';
import { UpgradeText } from '@/components/UpgradeText/UpgradeText';
import { useContext, useEffect } from 'react';
import { VoucherInput } from '@/components/VoucherInput/VoucherInput';
import { VoucherPrompt } from '@/components/VoucherPrompt/VoucherPrompt';
import { VoucherText } from '@/components/VoucherText/VoucherText';
import authentication from '@/lib/authentication';
import { shouldShowUpdatedPriceBanner, shouldShowUpgradeText, type BannerActionType } from '@/lib/banner';

type Props = {
  bannerAction?: BannerActionType;
  showActiveProductWarning?: boolean;
};

const ProductAttributes = ({ bannerAction, showActiveProductWarning }: Props) => {
  const [ state, dispatch ] = useContext(Context);

  useEffect(() => {
    void (async () => {
      const isLoggedIn = await authentication.userIsLoggedIn();
      dispatch({
        type: ActionTypes.isLoggedIn,
        payload: isLoggedIn
      });
    })().catch(() => {
      // Assume not logged in on error
      dispatch({
        type: ActionTypes.isLoggedIn,
        payload: false
      });
    });
  }, [ dispatch ]);

  /**
   * Shows voucher prompt, input, or text based on the available information.
   * If the user is buying a supporter product, nothing is shown.
   *
   * @returns {React.ReactNode | null}
   */
  function getVoucherElement(): React.ReactNode | null {
    if (isSupporter(state.bundle)) {
      return null;
    }

    if (state.voucher?.code) {
      return <VoucherText />;
    } else if (state.showVoucherInput) {
      return <VoucherInput />;
    } else if (state.currentPage === 'paymentPage') {
      return <VoucherPrompt />;
    } else {
      return null;
    }
  }

  return (
    <div className="product-attributes">
      {shouldShowUpdatedPriceBanner(state, bannerAction, showActiveProductWarning) ? (
        <Banner className="banner-neutral">{languageStrings.product.updatedPrice}</Banner>
      ) : null}
      <ProductPrice />
      {getVoucherElement()}
      {shouldShowUpgradeText(bannerAction, showActiveProductWarning) ? <UpgradeText /> : null}
    </div>
  );
};

export { ProductAttributes };
